import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Divider,
  Flex,
  GridItem,
  Image,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import {
  getPrettyAircraftType,
  interleave,
  prettyCurrency,
  prettyNumber,
  prettyStopCount,
  RoundingModeOptions,
} from '@pelicargo/utils';
import { AircraftType, LineItemType, VariantType } from '@prisma/client';
import { ReactNode, useEffect, useMemo } from 'react';
import { HiChevronRight } from 'react-icons/hi2';
import { Link as RRLink, useNavigate } from 'react-router-dom';

import { trpc } from '../../config/trpc';
import { goToQuote, useQuoteHelper } from '../../hooks/quotes/useQuoteHelper';
import { useQuoteVariantHelper } from '../../hooks/useQuoteVariantHelper';
import { useSelectedQuoteVariant } from '../../hooks/useSelectedQuoteVariant';
import { API } from '../../utils/apiTypes';
import { getBestQuoteVariants } from '../../utils/getTotalCost';
import { AirportStops } from '../AirportStops';
import { ConditionalTags } from '../ConditionalTags';
import { FlightSegmentsCard } from '../FlightSegmentsCard';
import { IconPivotWeight } from '../Icons/IconPivotWeight';
import { QuoteVariantTabs } from '../QuoteVariantTabs';

type Props = {
  quote: API.QuoteWithTotalCost;
  selectedCargoType?: VariantType;
};

export const QuoteListItem = ({ quote, selectedCargoType }: Props) => {
  const { selectedQuoteVariantId, setSelectedQuoteVariantId } = useSelectedQuoteVariant();

  const bestQuoteVariants = getBestQuoteVariants(quote?.quote_variants || [], selectedQuoteVariantId);
  const cheapestQuoteVariant = bestQuoteVariants?.[0];
  const quoteVariantByType = quote?.quote_variants?.find((variant) => variant.type === selectedCargoType);
  const selectedQuoteVariant = bestQuoteVariants?.find((variant) => variant.id === selectedQuoteVariantId);
  const quoteVariant = selectedQuoteVariant || quoteVariantByType || cheapestQuoteVariant;

  useEffect(() => {
    if (quoteVariant?.id) {
      setSelectedQuoteVariantId(quoteVariant.id);
    }
  }, [quoteVariant?.id, setSelectedQuoteVariantId]);

  const navigate = useNavigate();

  const quoteHelper = useQuoteHelper(quote);
  const quoteVariantHelper = useQuoteVariantHelper(quoteVariant, quote);

  const { isBookableStage, isExpired, stopCount, mostConstrainedLeg } = quoteHelper;
  const {
    hasInlandService,
    exclusiveVariableRate,
    allInVariableRate,
    airFreightAllInVariableRate,
    inlandAllInVariableRate,

    screeningRate,
    otherFeesTotal,
    airFreightTotal,
    inlandTotal,
    totalCost,
  } = quoteVariantHelper;

  const isScreeningRatePerUnit = useMemo(() => {
    return quoteVariant?.line_items?.some((item) => item.type === LineItemType.SCREENING && !!item.per_unit_cost);
  }, [quoteVariant?.line_items]);

  const expansionDisclosure = useDisclosure();

  // RESPONSIVE BREAK POINTS
  const isHeaderFlightInfoVisible = useBreakpointValue({ base: false, sm: true });
  const isVerticalDividerVisible = useBreakpointValue({ base: !isBookableStage, sm: true });
  const isCardLink = useBreakpointValue({ base: true, sm: false });

  // EVENT HANDLERS
  const navigateToBooking = () => {
    navigate(goToQuote(quote, { id: selectedQuoteVariantId }));
  };

  const handleCardClick = () => {
    if (isCardLink) return navigateToBooking();

    return expansionDisclosure.onToggle();
  };

  const visibleAircraftType = useMemo(() => {
    if (quote?.flight_path?.some((node) => node.aircraft_type === AircraftType.RFS)) {
      return getPrettyAircraftType(AircraftType.RFS);
    }

    return getPrettyAircraftType(mostConstrainedLeg?.aircraft_type);
  }, [mostConstrainedLeg?.aircraft_type, quote?.flight_path]);

  const FeeBreakdown = () => {
    const hasInland = !!quoteVariant?.inland_service_quote_variant;

    if (hasInland) {
      return (
        <>
          <LabeledValue
            label={`${prettyCurrency(airFreightTotal)} airline`}
            value={`${prettyCurrency(airFreightAllInVariableRate)}/kg`}
          />
          <LabeledValue
            label={`${prettyCurrency(inlandTotal)} inland`}
            value={`${prettyCurrency(inlandAllInVariableRate)}/kg`}
          />
        </>
      );
    }

    const screeningCharge = screeningRate
      ? `+${prettyCurrency(screeningRate)}${isScreeningRatePerUnit ? '/kg' : ''} scrn`
      : 'air freight';

    const isPivotWeight = quote?.request?.chargeable_weight !== quote?.chargeable_weight;

    const maybePivotWeight = isPivotWeight ? (
      <Stack direction="row" align="center">
        <IconPivotWeight />
        <Text>pivot weight</Text>
      </Stack>
    ) : (
      'charge weight'
    );

    return (
      <>
        <LabeledValue
          label={maybePivotWeight}
          value={`${prettyNumber(quote.chargeable_weight, { roundingMode: RoundingModeOptions.Ceil })}kg`}
        />
        <LabeledValue label={screeningCharge} value={`${prettyCurrency(exclusiveVariableRate)}/kg`} />
        <Show if={!!otherFeesTotal && otherFeesTotal !== totalCost}>
          <LabeledValue label="other fees" value={prettyCurrency(otherFeesTotal)} />
        </Show>
      </>
    );
  };

  const { data: backlogData, refetch } = trpc.isQuoteBacklogged.useQuery({ id: quote.id });

  const airports = useMemo(() => {
    return Array.from(
      quote?.flight_path?.reduce((agg, fp) => {
        agg.add(fp.origin_airport_id);
        agg.add(fp.destination_airport_id);
        return agg;
      }, new Set<string>()),
    );
  }, [quote?.flight_path]);

  trpc.onBacklogUpdate.useSubscription({ provider_id: quote.provider_id, airports }, { onData: () => refetch() });

  const warnings: string[] = [];
  if (isExpired) warnings.push('Expired');

  return (
    <Card
      p={0}
      borderRadius="lg"
      transition="border-color 200ms ease"
      overflow="hidden"
      borderColor="border.default"
      _hover={{
        borderWidth: 1,
        borderColor: isBookableStage ? 'border.hover' : 'border.default',
        overflow: 'hidden',
      }}
    >
      <CardBody
        p={0}
        m={0}
        borderRadius="lg"
        cursor={isBookableStage ? 'pointer' : 'initial'}
        onClick={handleCardClick}
      >
        <Stack
          direction={{ base: 'column', sm: 'row' }}
          spacing={0}
          p={0}
          divider={!isVerticalDividerVisible && <Divider />}
        >
          <Stack
            spacing={1.5}
            py={0}
            px={4}
            borderTopLeftRadius="lg"
            borderRightWidth={isBookableStage ? 1 : 0}
            borderBottomLeftRadius="lg"
            justify="space-between"
          >
            <Stack direction="row" align="center" minH={6}>
              <ConditionalTags quote={quote} quoteVariant={quoteVariant} backlogs={backlogData?.backlogs} />
            </Stack>
            <Stack
              direction="row"
              justify={{ base: 'space-between', md: 'left' }}
              align="bottom"
              wrap="wrap"
              spacing={{ base: '4', lg: '8' }}
            >
              <Skeleton isLoaded={!!quote?.provider}>
                <Image src={quote?.provider?.logo_uri} height="40px" width="40px" alt={quote?.provider?.name} />
              </Skeleton>

              <Stack direction="row" w="280px">
                <AirportStops flightSegments={quote.flight_path} w="full" />
              </Stack>

              <Show if={isHeaderFlightInfoVisible}>
                <SimpleGrid columns={3} columnGap={4} alignItems="center">
                  <GridItem colSpan={1} height="fit-content" minW={24}>
                    <Text textStyle="bodySmallRegular">{prettyStopCount(stopCount)}</Text>
                  </GridItem>
                  <Show if={!!quote?.estimated_transit_time}>
                    <GridItem colSpan={1} height="fit-content" minW={24}>
                      <Text textStyle="bodySmallRegular">{quote?.estimated_transit_time}</Text>
                    </GridItem>
                  </Show>
                  <GridItem colSpan={1} height="fit-content" minW={24}>
                    <Text textStyle="bodySmallRegular">{visibleAircraftType}</Text>
                  </GridItem>
                </SimpleGrid>
              </Show>
            </Stack>
            <Stack direction="row" align="center" minH={6}>
              <Skeleton isLoaded={!!quote?.provider} noOfLines={1} w={!quote?.provider ? 20 : 'full'}>
                <Text textStyle="captionSmallRegular" color="text.subtle">
                  {quote?.provider?.name}
                </Text>
              </Skeleton>
            </Stack>
          </Stack>
          <Stack
            justify="space-between"
            spacing={1.5}
            px={4}
            flex={1}
            backgroundColor="bg.surface !important"
            _hover={{ backgroundColor: 'bg.surface !important' }}
          >
            <Show if={isBookableStage}>
              <Stack
                direction={{ base: 'column', lg: 'row' }}
                divider={<Divider orientation="vertical" />}
                h="full"
                w="full"
                justify="space-between"
                gap={{ base: '1', sm: '3' }}
              >
                <Flex flexDir="column" justifyContent="center">
                  <Stack
                    direction="row"
                    align="center"
                    justify="flex-end"
                    spacing={4}
                    minW={{ base: 'unset', sm: 'xs', md: 'sm', lg: 'sm' }}
                    wrap="wrap"
                  >
                    <FeeBreakdown />
                  </Stack>
                  <Flex direction="row" marginLeft="auto" gap="1">
                    {interleave(
                      warnings.map((warning) => (
                        <Text textStyle="captionSmallMedium" color="error" key={warning}>
                          {warning}
                        </Text>
                      )),
                      <Text textStyle="captionSmallMedium" color="error">
                        •
                      </Text>,
                    )}
                  </Flex>
                </Flex>
                <Stack direction="column" spacing="1" h="full" justify="center" py={3}>
                  <Stack direction="column" spacing={0}>
                    <Show if={hasInlandService}>
                      <Stack direction="row" align="baseline" spacing="1">
                        <Text fontSize="2xl" fontWeight="500">
                          {prettyCurrency(allInVariableRate)}
                        </Text>
                        <Text textStyle="bodySmallMedium" color="gray">
                          /kg
                        </Text>
                      </Stack>
                      <Stack direction="row" align="baseline" spacing="1" mt={-0.5}>
                        <Text fontSize="sm" fontWeight="500">
                          {prettyCurrency(totalCost)}
                        </Text>
                        <Text fontSize="xs" color="gray">
                          total
                        </Text>
                      </Stack>
                    </Show>
                    <Show if={!hasInlandService}>
                      <Stack direction="row" align="end" spacing="1" justifySelf="center">
                        <Text fontSize="2xl" fontWeight="500">
                          {prettyCurrency(totalCost)}
                        </Text>
                        <Text textStyle="bodySmallMedium" color="gray">
                          total
                        </Text>
                      </Stack>
                    </Show>
                  </Stack>

                  <Button
                    as={RRLink}
                    to={goToQuote(quote, { id: selectedQuoteVariantId })}
                    colorScheme="primary"
                    size="sm"
                    iconSpacing="2"
                    minW="113px"
                    rightIcon={<HiChevronRight strokeWidth="2" />}
                  >
                    View Quote
                  </Button>
                </Stack>
              </Stack>
            </Show>
          </Stack>
        </Stack>
      </CardBody>
      <Show if={expansionDisclosure.isOpen && isBookableStage}>
        <CardFooter
          bg="bg.subtle"
          display={{ base: 'none', md: 'flex' }}
          p="3"
          borderBottomStartRadius="lg"
          borderBottomEndRadius="lg"
        >
          <Stack
            direction={{ base: 'column', xl: 'row' }}
            align="center"
            justify="flex-start"
            w="full"
            p="0"
            spacing="3"
          >
            <FlightSegmentsCard
              flightSegments={quote?.flight_path}
              estimatedTransitTime={quote?.estimated_transit_time}
              h="full"
              flex={1}
            />
            <Card flex={1} h="full">
              <CardBody p="0">
                <QuoteVariantTabs quote={quote} quoteVariants={bestQuoteVariants} />
              </CardBody>
            </Card>
          </Stack>
        </CardFooter>
      </Show>
    </Card>
  );
};

type LabeledValueProps = {
  label: ReactNode;
  value?: any;
};
const LabeledValue = ({ label, value }: LabeledValueProps) => {
  return (
    <Stack direction="column" align="right" spacing={1} minW={{ base: 'fit-content', md: '90px' }}>
      <Text textAlign="right">{value}</Text>
      <Text textStyle="captionSmallMedium" color="text.subtle" textAlign="right">
        {label}
      </Text>
    </Stack>
  );
};
